import "@fontsource-variable/inter";

import { createTheme, type Theme } from "@mui/material/styles";
import { zhCN as materialZh } from "@mui/material/locale";
import type {} from "@mui/x-charts/themeAugmentation";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { zhCN as gridZh } from "@mui/x-data-grid/locales";

import { components } from "./components";
import { accent, danger, font, neutral, radius, success, warning } from "./tokens";

export { mono } from "./charts";

/**
 * 全站主题。只覆盖确实用到的组件，密度与色值全部取自 ./tokens。
 * 设计取向见 tokens.ts 的说明；业务页面不写死色值与像素。
 */
export const theme = createTheme(
  {
    cssVariables: { colorSchemeSelector: "data-mui-color-scheme", cssVarPrefix: "fx" },
    shape: { borderRadius: radius.md },
    // 企业后台不靠投影分层，只用 1px 边框；保留两级极轻的浮层阴影给弹层使用。
    shadows: [
      "none",
      "0 1px 2px rgba(16, 24, 40, 0.06)",
      "0 4px 12px rgba(16, 24, 40, 0.08)",
      ...Array<string>(22).fill("0 8px 24px rgba(16, 24, 40, 0.12)"),
    ] as unknown as Theme["shadows"],
    colorSchemes: {
      light: {
        palette: {
          mode: "light",
          primary: { main: accent[500], light: accent[300], dark: accent[700], contrastText: "#fff" },
          success: { main: success[500], light: success[300], dark: success[700], contrastText: "#fff" },
          warning: { main: warning[400], light: warning[200], dark: warning[700], contrastText: "#fff" },
          error: { main: danger[500], light: danger[300], dark: danger[700], contrastText: "#fff" },
          info: { main: accent[500], light: accent[300], dark: accent[700], contrastText: "#fff" },
          background: { default: neutral[25], paper: neutral[0] },
          text: { primary: neutral[900], secondary: neutral[600], disabled: neutral[400] },
          divider: neutral[150],
          action: { hover: neutral[50], selected: accent[50], disabledOpacity: 0.45 },
        },
      },
      dark: {
        palette: {
          mode: "dark",
          primary: { main: accent[400], light: accent[300], dark: accent[600], contrastText: "#fff" },
          success: { main: success[400], light: success[300], dark: success[600], contrastText: "#fff" },
          warning: { main: warning[300], light: warning[200], dark: warning[500], contrastText: neutral[950] },
          error: { main: danger[400], light: danger[300], dark: danger[600], contrastText: "#fff" },
          info: { main: accent[400], light: accent[300], dark: accent[600], contrastText: "#fff" },
          background: { default: neutral[950], paper: neutral[900] },
          text: { primary: neutral[50], secondary: neutral[400], disabled: neutral[600] },
          divider: neutral[800],
          action: { hover: neutral[850], selected: accent[900], disabledOpacity: 0.45 },
        },
      },
    },
    typography: {
      fontFamily: font.sans,
      fontSize: 13,
      htmlFontSize: 16,
      // 标题靠字重和字距拉层次，字号保持克制，避免页面被大标题切碎。
      h1: { fontSize: 20, fontWeight: 600, letterSpacing: -0.2, lineHeight: 1.3 },
      h2: { fontSize: 16, fontWeight: 600, letterSpacing: -0.1, lineHeight: 1.35 },
      h3: { fontSize: 14, fontWeight: 600, lineHeight: 1.4 },
      h4: { fontSize: 13, fontWeight: 600, lineHeight: 1.4 },
      h5: { fontSize: 13, fontWeight: 600, lineHeight: 1.4 },
      h6: { fontSize: 13, fontWeight: 600, lineHeight: 1.4 },
      subtitle1: { fontSize: 13, fontWeight: 500, lineHeight: 1.5 },
      subtitle2: { fontSize: 12, fontWeight: 600, lineHeight: 1.5 },
      body1: { fontSize: 13, lineHeight: 1.55 },
      body2: { fontSize: 13, lineHeight: 1.5 },
      caption: { fontSize: 12, lineHeight: 1.45 },
      overline: { fontSize: 11, fontWeight: 600, letterSpacing: 0.6, lineHeight: 1.4, textTransform: "uppercase" },
      button: { fontSize: 13, fontWeight: 500, textTransform: "none", letterSpacing: 0 },
    },
    components,
  },
  materialZh,
  gridZh,
);
